import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { HelpCircle, Menu } from "lucide-react";
import { CommandPaletteStub } from "./CommandPaletteStub";
import { CreateMenu } from "@/components/home/CreateMenu";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { UserMenu } from "./UserMenu";
import { HelpDrawer } from "@/components/assistant/HelpDrawer";
import { useAuthStore } from "@/store/useAuthStore";

/**
 * D-124 M1 — `onMenuOpen` / `menuOpen` are wired by AppLayout to the off-canvas
 * mobile nav drawer. The hamburger is `md:hidden` (the sidebar takes over at md);
 * desktop topbar is unchanged.
 */
export function Topbar({
  onMenuOpen,
  menuOpen = false,
}: {
  onMenuOpen?: () => void;
  menuOpen?: boolean;
} = {}) {
  // AI-1 — глобальний «?»-помічник (контекст екрана через whitelist-метадані).
  const [helpOpen, setHelpOpen] = useState(false);
  // §20 fix (Task Mgmt v1.2 review): the old layout/CreateMenu had 5 DEAD items
  // (no onClick at all). Topbar now reuses the CANONICAL allowedActions-gated
  // CreateMenu (Задача/Запит у відділ/Зустріч → canonical drawers, D-031).
  const allowedActions = useAuthStore((s) => s.permissions?.allowedActions) ?? {
    task: true,
    internal_request: false,
    meeting: false,
  };
  const qc = useQueryClient();
  const onDone = () => {
    qc.invalidateQueries({ queryKey: ["tasks"] });
    qc.invalidateQueries({ queryKey: ["homeSummary"] });
  };

  return (
    <header className="relative z-30 h-14 shrink-0 flex items-center gap-3 border-b bg-background px-4">
      <div className="flex-1 flex items-center gap-2">
        {/* M1 — mobile nav entry (hidden md+: desktop uses the sidebar). */}
        <button
          type="button"
          onClick={onMenuOpen}
          aria-label="Відкрити меню"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          aria-controls="mobile-nav-drawer"
          className="md:hidden -ml-1 flex h-10 w-10 items-center justify-center rounded-lg text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Menu className="h-[22px] w-[22px]" aria-hidden />
        </button>
        {/* Command palette — desktop only (mobile topbar = hamburger + actions). */}
        <div className="hidden flex-1 items-center md:flex">
          <CommandPaletteStub />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <CreateMenu allowedActions={allowedActions} onDone={onDone} />
        {/* D-043 S2 — live bell (real unread count + dropdown), no more hardcoded "3". */}
        <NotificationBell />
        {/* AI-1 — «?»-помічник; on mobile it lives in the drawer footer (gated), not the topbar (TZ §1/§3). */}
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          aria-label="Помічник по роботі з CRM"
          aria-haspopup="dialog"
          className="hidden h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:flex"
        >
          <HelpCircle className="h-5 w-5" aria-hidden />
        </button>
        <UserMenu />
      </div>
      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}
